import { YES_NO, applicantSection, otherAttachments, replySection } from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFieldOption, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * H3. Wniosek o wydanie orzeczenia o niepełnosprawności (dziecko do 16 lat), o stopniu niepełnosprawności albo
 * o wskazaniach do ulg i uprawnień — art. 6–6b¹ ustawy o rehabilitacji; treść wniosku i załączniki: § 2 i § 6
 * rozporządzenia MGPiPS z 15.07.2003 r. (tekst z api.sejm.gov.pl/eli/acts/DU/2021/857 pobrany 2026-10-22). Rozporządzenie
 * nie ustala wzoru; układ jak wzory publikowane na gov.pl — wniosek dla osób powyżej 16 lat:
 * https://www.gov.pl/attachment/93cc2993-a393-4827-836e-7d17ebdbc35d (pobrany 2026-10-22) — oraz wzór dla dzieci ZOON
 * Słupsk: https://bip.zoon.slupsk.pl/40.html?file=634 (pobrany 2026-10-22). Pominięto pytania wzoru o samodzielność
 * i sprzęt rehabilitacyjny (dane o zdrowiu ocenia skład orzekający z dokumentacji medycznej). Formularz nie obejmuje
 * pełnomocnika — wzory przewidują osobę zainteresowaną albo jej przedstawiciela ustawowego, opiekuna lub kuratora
 * (§ 6 ust. 1); pełnomocnictwo można dołączyć w „Inne załączniki”. Walidacji „zaświadczenie ≤ 30 dni” silnik nie ma —
 * jest pole z datą i podpowiedź.
 */
const CHILD: EbiCondition = { field: "rodzajOrzeczenia", equals: "dziecko" };
const DEGREE: EbiCondition = { field: "rodzajOrzeczenia", equals: "stopien" };
const RELIEFS: EbiCondition = { field: "rodzajOrzeczenia", equals: "ulgi" };
const ADULT: EbiCondition = { not: CHILD };
/** Orzeczenie o niepełnosprawności lub stopniu — wymaga zaświadczenia lekarskiego i dokumentacji (§ 6 ust. 2). */
const MEDICAL: EbiCondition = { any: [CHILD, DEGREE] };
const ADULT_REPRESENTATIVE: EbiCondition = { all: [ADULT, { field: "skladajacy", equals: "przedstawiciel" }] };
const OTHER_PERSON: EbiCondition = { any: [CHILD, ADULT_REPRESENTATIVE] };
const NO_PESEL: EbiCondition = { field: "osobaBrakPesel", equals: true };
const COURT: EbiCondition = {
  any: [{ all: [CHILD, { field: "przedstawicielDziecka", equals: "opiekun" }] }, ADULT_REPRESENTATIVE],
};

/** Cele orzeczenia dla osób powyżej 16 lat (wzór z gov.pl). */
const ADULT_PURPOSES: IEbiFieldOption[] = [
  { value: "zatrudnienie", label: "odpowiedniego zatrudnienia" },
  { value: "szkolenie", label: "szkolenia" },
  { value: "zaz", label: "zatrudnienia w zakładzie aktywności zawodowej" },
  { value: "wtz", label: "uczestnictwa w warsztatach terapii zajęciowej" },
  { value: "zaopatrzenie", label: "konieczności zaopatrzenia w przedmioty ortopedyczne, środki pomocnicze lub pomoce techniczne" },
  { value: "wsparcie", label: "korzystania z systemu środowiskowego wsparcia w samodzielnej egzystencji" },
  { value: "zasilekStaly", label: "ubiegania się o zasiłek stały" },
  { value: "zasilekPielegnacyjny", label: "ubiegania się o zasiłek pielęgnacyjny" },
  { value: "oddzielnyPokoj", label: "prawa do zamieszkiwania w oddzielnym pokoju" },
  { value: "inne", label: "inne" },
];

const H3: IEbiFormDefinition = {
  code: "H3",
  version: 1,
  title: "Wniosek o wydanie orzeczenia o niepełnosprawności lub o stopniu niepełnosprawności",
  category: EEbiFormCategory.Disability,
  office: EEbiFormOffice.CountyUnit,
  description:
    "Orzeczenie o niepełnosprawności dziecka do 16 lat, o stopniu niepełnosprawności albo o wskazaniach do ulg i uprawnień — wydaje powiatowy zespół do spraw orzekania o niepełnosprawności.",
  keywords: ["orzeczenie", "niepełnosprawność", "stopień niepełnosprawności", "ulgi i uprawnienia", "zespół orzekający", "PZON", "legitymacja"],
  organ: "Powiatowy (Miejski) Zespół do Spraw Orzekania o Niepełnosprawności właściwy według miejsca stałego pobytu osoby zainteresowanej",
  legalBasis:
    "art. 6–6b¹ ustawy z dnia 27 sierpnia 1997 r. o rehabilitacji zawodowej i społecznej oraz zatrudnianiu osób niepełnosprawnych; § 2, § 3 i § 6 rozporządzenia Ministra Gospodarki, Pracy i Polityki Społecznej z dnia 15 lipca 2003 r. w sprawie orzekania o niepełnosprawności i stopniu niepełnosprawności",
  request: "Wnoszę o wydanie orzeczenia wskazanego we wniosku dla osoby zainteresowanej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty.",
  deadlineLabel: "do miesiąca",
  deadline: "Do miesiąca, w sprawach szczególnie skomplikowanych do dwóch miesięcy (art. 35 KPA).",
  variantField: "rodzajOrzeczenia",
  variants: {
    dziecko: { title: "Wniosek o wydanie orzeczenia o niepełnosprawności (dziecko do 16 roku życia)" },
    stopien: { title: "Wniosek o wydanie orzeczenia o stopniu niepełnosprawności" },
    ulgi: { title: "Wniosek o wydanie orzeczenia o wskazaniach do ulg i uprawnień" },
  },
  sections: [
    {
      code: "rodzajWniosku",
      title: "Wnoszę o wydanie",
      fields: [
        {
          code: "rodzajOrzeczenia",
          type: "radio",
          label: "Orzeczenie",
          required: true,
          options: [
            { value: "dziecko", label: "o niepełnosprawności — dziecko, które nie ukończyło 16 roku życia" },
            { value: "stopien", label: "o stopniu niepełnosprawności — osoba, która ukończyła 16 rok życia" },
            {
              value: "ulgi",
              label: "o wskazaniach do ulg i uprawnień — osoba posiadająca orzeczenie o niezdolności do pracy lub o grupie inwalidzkiej (ZUS, KRUS lub inny organ rentowy)",
            },
          ],
        },
        {
          code: "trybWniosku",
          type: "radio",
          label: "Wniosek składam",
          required: true,
          visibleWhen: MEDICAL,
          defaultValue: "pierwszy",
          options: [
            { value: "pierwszy", label: "pierwszy raz — osoba nie ma ustalonej niepełnosprawności ani stopnia niepełnosprawności" },
            { value: "ponowny", label: "ponownie — dotychczasowe orzeczenie wydano na czas określony" },
            { value: "zmiana", label: "ponownie — w związku ze zmianą stanu zdrowia" },
          ],
        },
      ],
    },
    applicantSection(),
    {
      code: "kto",
      title: "Kto składa wniosek",
      fields: [
        {
          code: "przedstawicielDziecka",
          type: "radio",
          label: "Wniosek w imieniu dziecka składam jako",
          required: true,
          visibleWhen: CHILD,
          hint: "Wniosek dotyczący dziecka, które nie ukończyło 16 roku życia, składa jego przedstawiciel ustawowy.",
          options: [
            { value: "rodzic", label: "rodzic dziecka" },
            { value: "opiekun", label: "opiekun prawny dziecka ustanowiony przez sąd" },
          ],
        },
        {
          code: "skladajacy",
          type: "radio",
          label: "Wniosek składam",
          required: true,
          visibleWhen: ADULT,
          defaultValue: "osoba",
          options: [
            { value: "osoba", label: "we własnej sprawie — wniosek dotyczy mnie" },
            { value: "przedstawiciel", label: "jako przedstawiciel ustawowy, opiekun prawny lub kurator osoby zainteresowanej" },
          ],
        },
      ],
    },
    {
      code: "osobaZainteresowana",
      title: "Osoba zainteresowana (dziecko)",
      description:
        "Dane osoby, której dotyczy orzeczenie (§ 6 ust. 1 rozporządzenia). Gdy wniosek dotyczy Ciebie, imię, nazwisko, PESEL i adres pochodzą z części „Wnioskodawca”.",
      fields: [
        { code: "osobaImieNazwisko", type: "text", label: "Imię i nazwisko", required: true, visibleWhen: OTHER_PERSON, maxLength: 200 },
        {
          code: "osobaBrakPesel",
          type: "checkbox",
          label: "Osoba zainteresowana nie ma numeru PESEL (cudzoziemiec)",
          visibleWhen: OTHER_PERSON,
        },
        { code: "osobaPesel", type: "pesel", label: "PESEL", required: true, visibleWhen: { all: [OTHER_PERSON, { not: NO_PESEL }] }, width: "half" },
        {
          code: "osobaObywatelstwo",
          type: "text",
          label: "Obywatelstwo",
          required: true,
          visibleWhen: { all: [OTHER_PERSON, NO_PESEL] },
          width: "half",
          maxLength: 100,
        },
        {
          code: "osobaAdres",
          type: "text",
          label: "Miejsce stałego pobytu albo pobytu — ulica, numer domu i lokalu",
          required: true,
          visibleWhen: OTHER_PERSON,
          maxLength: 200,
        },
        { code: "osobaKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, visibleWhen: OTHER_PERSON, width: "third", placeholder: "00-000" },
        { code: "osobaMiejscowosc", type: "text", label: "Miejscowość", required: true, visibleWhen: OTHER_PERSON, width: "twoThirds", maxLength: 100 },
        { code: "dataUrodzenia", type: "date", label: "Data urodzenia", required: true, width: "half" },
        { code: "miejsceUrodzenia", type: "text", label: "Miejsce urodzenia", required: true, width: "half", maxLength: 100 },
        {
          code: "dokumentTozsamosci",
          type: "text",
          label: "Rodzaj, seria i numer dokumentu potwierdzającego tożsamość",
          requiredWhen: ADULT,
          maxLength: 100,
          placeholder: "np. dowód osobisty ABC 123456",
          hint: "Dziecko — jeśli posiada (np. legitymacja szkolna, paszport).",
        },
      ],
    },
    {
      code: "cel",
      title: "Cel wydania orzeczenia",
      fields: [
        {
          code: "celeDziecko",
          type: "checkboxes",
          label: "Orzeczenie jest niezbędne do",
          required: true,
          visibleWhen: CHILD,
          options: [
            { value: "zasilekPielegnacyjny", label: "zasiłku pielęgnacyjnego" },
            { value: "swiadczeniePielegnacyjne", label: "świadczenia pielęgnacyjnego" },
            { value: "oddzielnyPokoj", label: "zamieszkiwania w oddzielnym pokoju" },
            { value: "urlopWychowawczy", label: "uzyskania urlopu wychowawczego w dodatkowym wymiarze" },
            { value: "ulgi", label: "korzystania z innych ulg i uprawnień na podstawie odrębnych przepisów (w tym karty parkingowej)" },
            { value: "inne", label: "inne" },
          ],
        },
        { code: "cele", type: "checkboxes", label: "Orzeczenie jest niezbędne do", required: true, visibleWhen: ADULT, options: ADULT_PURPOSES },
        {
          code: "celInny",
          type: "text",
          label: "Inny cel",
          required: true,
          visibleWhen: {
            any: [
              { all: [CHILD, { field: "celeDziecko", includes: "inne" }] },
              { all: [ADULT, { field: "cele", includes: "inne" }] },
            ],
          },
          maxLength: 300,
        },
        {
          code: "celPodstawowy",
          type: "select",
          label: "Cel podstawowy",
          visibleWhen: ADULT,
          hint: "Nieobowiązkowo — jeden z zaznaczonych celów, najważniejszy dla Ciebie.",
          options: ADULT_PURPOSES,
        },
      ],
    },
    {
      code: "sytuacja",
      title: "Sytuacja społeczna i zawodowa",
      visibleWhen: MEDICAL,
      fields: [
        {
          code: "placowkaDziecka",
          type: "select",
          label: "Dziecko uczęszcza",
          required: true,
          visibleWhen: CHILD,
          options: [
            { value: "zlobek", label: "do żłobka" },
            { value: "przedszkole", label: "do przedszkola ogólnodostępnego" },
            { value: "przedszkoleIntegracyjne", label: "do przedszkola integracyjnego" },
            { value: "przedszkoleSpecjalne", label: "do przedszkola specjalnego" },
            { value: "szkola", label: "do szkoły ogólnodostępnej" },
            { value: "klasaIntegracyjna", label: "do klasy integracyjnej w szkole ogólnodostępnej" },
            { value: "klasaSpecjalna", label: "do klasy specjalnej w szkole ogólnodostępnej" },
            { value: "szkolaIntegracyjna", label: "do szkoły integracyjnej" },
            { value: "szkolaSpecjalna", label: "do szkoły specjalnej" },
            { value: "osrodek", label: "do ośrodka szkolno-wychowawczego" },
            { value: "nie", label: "nie uczęszcza do żadnej z tych placówek" },
          ],
        },
        { code: "stanCywilny", type: "text", label: "Stan cywilny", required: true, visibleWhen: DEGREE, width: "half", maxLength: 100 },
        {
          code: "stanRodzinny",
          type: "text",
          label: "Stan rodzinny",
          visibleWhen: DEGREE,
          width: "half",
          maxLength: 200,
          placeholder: "np. mieszkam z małżonkiem i dzieckiem",
        },
        { code: "wyksztalcenie", type: "text", label: "Wykształcenie", required: true, visibleWhen: DEGREE, width: "half", maxLength: 200 },
        { code: "zawod", type: "text", label: "Zawód", visibleWhen: DEGREE, width: "half", maxLength: 200 },
        {
          code: "zatrudnienie",
          type: "text",
          label: "Obecne zatrudnienie",
          required: true,
          visibleWhen: DEGREE,
          maxLength: 300,
          placeholder: "np. niezatrudniona/niezatrudniony, emerytka/emeryt, nazwa zakładu pracy",
        },
      ],
    },
    {
      code: "zaswiadczenie",
      title: "Zaświadczenie lekarskie",
      visibleWhen: MEDICAL,
      fields: [
        {
          code: "dataZaswiadczenia",
          type: "date",
          label: "Data wystawienia zaświadczenia lekarskiego o stanie zdrowia",
          required: true,
          width: "half",
          hint: "Zaświadczenie musi być wydane nie wcześniej niż 30 dni przed złożeniem wniosku (§ 6 ust. 2 rozporządzenia).",
        },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        {
          code: "swiadczenie",
          type: "radio",
          label: "Świadczenia (np. renta, emerytura, zasiłek)",
          required: true,
          visibleWhen: ADULT,
          options: [
            { value: "pobieram", label: "pobieram świadczenie" },
            { value: "niePobieram", label: "nie pobieram świadczenia" },
          ],
        },
        {
          code: "swiadczenieJakie",
          type: "text",
          label: "Jakie świadczenie i z jakiego organu",
          required: true,
          visibleWhen: { all: [ADULT, { field: "swiadczenie", equals: "pobieram" }] },
          maxLength: 300,
        },
        { code: "wczesniejszyWniosek", type: "radio", label: "Czy składano wcześniej wniosek o wydanie orzeczenia?", required: true, options: YES_NO },
        {
          code: "wczesniejszyWniosekSkutek",
          type: "text",
          label: "Kiedy i z jakim skutkiem (numer i data orzeczenia, okres ważności)",
          required: true,
          visibleWhen: { field: "wczesniejszyWniosek", equals: "tak" },
          maxLength: 300,
        },
        {
          code: "posiedzenie",
          type: "radio",
          label: "Udział w posiedzeniu składu orzekającego",
          required: true,
          options: [
            { value: "moze", label: "osoba zainteresowana (dziecko) może samodzielnie przybyć na posiedzenie" },
            { value: "nieMoze", label: "osoba zainteresowana (dziecko) nie może przybyć na posiedzenie" },
          ],
        },
        {
          code: "pouczenie41",
          type: "declaration",
          label:
            "Zostałam/zostałem pouczona/pouczony o treści art. 41 § 1 i 2 Kodeksu postępowania administracyjnego: w toku postępowania strony oraz ich przedstawiciele i pełnomocnicy mają obowiązek zawiadomić organ administracji publicznej o każdej zmianie swojego adresu, w tym adresu elektronicznego; w razie zaniedbania tego obowiązku doręczenie pisma pod dotychczasowym adresem ma skutek prawny.",
          required: true,
        },
        {
          code: "oswiadczeniePrawdziwosc",
          type: "declaration",
          label:
            "Oświadczam, że dane zawarte we wniosku są zgodne ze stanem faktycznym, i jestem świadoma/świadomy odpowiedzialności karnej za zeznanie nieprawdy lub zatajenie prawdy (art. 233 § 1 Kodeksu karnego).",
          required: true,
        },
        {
          code: "oswiadczenieDokumentacja",
          type: "declaration",
          label:
            "Oświadczam, że złożona dokumentacja jest autentyczna, i jestem świadoma/świadomy odpowiedzialności karnej za przestępstwa przeciwko wiarygodności dokumentów (art. 270 § 1 Kodeksu karnego).",
          required: true,
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "zaswiadczenieLekarskie",
      label: "Zaświadczenie lekarskie o stanie zdrowia wydane dla potrzeb zespołu do spraw orzekania o niepełnosprawności",
      hint: "Wystawia lekarz prowadzący, nie wcześniej niż 30 dni przed złożeniem wniosku; zespół może poprosić o oryginał.",
      visibleWhen: MEDICAL,
      required: true,
    },
    {
      code: "dokumentacjaMedyczna",
      label: "Dokumentacja medyczna",
      hint: "Karty informacyjne leczenia szpitalnego, wyniki badań, konsultacje specjalistyczne, opinie psychologiczne — skany lub zdjęcia (oryginały do wglądu na żądanie zespołu).",
      requiredWhen: MEDICAL,
      multiple: true,
    },
    {
      code: "orzeczenieRentowe",
      label: "Orzeczenie o niezdolności do pracy albo o zaliczeniu do grupy inwalidów (ZUS, KRUS lub inny organ rentowy)",
      hint: "§ 6 ust. 3 rozporządzenia.",
      visibleWhen: RELIEFS,
      required: true,
    },
    {
      code: "poprzednieOrzeczenie",
      label: "Poprzednie orzeczenie",
      hint: "Kopia — przy ponownym wniosku (praktyka zespołów).",
      visibleWhen: { all: [MEDICAL, { field: "trybWniosku", in: ["ponowny", "zmiana"] }] },
    },
    {
      code: "postanowienieSadu",
      label: "Postanowienie sądu o ustanowieniu opieki lub kurateli (albo o ubezwłasnowolnieniu)",
      hint: "Kopia postanowienia — gdy wniosek składa opiekun prawny, kurator lub przedstawiciel ustawowy osoby pełnoletniej.",
      visibleWhen: COURT,
      required: true,
    },
    {
      code: "zaswiadczenieNieobecnosc",
      label: "Zaświadczenie lekarskie o niemożności osobistego udziału w posiedzeniu składu orzekającego",
      hint: "Praktyka zespołów — dołącz, jeśli je posiadasz.",
      visibleWhen: { field: "posiedzenie", equals: "nieMoze" },
    },
    otherAttachments(),
  ],
};

export default H3;
